import { api, apiErrorMessage } from '@ddone/api-client';
import { useAuth } from '@ddone/auth';
import { Button } from '@ddone/ui';
import { CheckCircle2, MailCheck, ShieldCheck } from 'lucide-react';
import { useMemo, useState } from 'react';

interface InvitationAcceptance {
  workspaceId: string;
  name: string;
  slug: string;
  timezone: string;
  locale: string;
  currency: string;
  accepted: boolean;
}

interface AcceptInvitationScreenProps {
  onComplete(): Promise<void>;
  standalone?: boolean;
  navigateHome?(): void;
}

export function AcceptInvitationPage() {
  const { refreshSession } = useAuth();
  return <AcceptInvitationScreen onComplete={refreshSession} />;
}

export function AcceptInvitationScreen({
  onComplete,
  standalone = false,
  navigateHome = () => window.location.assign('/')
}: AcceptInvitationScreenProps) {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get('token')?.trim() ?? '',
    []
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [accepted, setAccepted] = useState<InvitationAcceptance>();

  const accept = async () => {
    if (!token) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const result = await api.request<InvitationAcceptance>('/invitations/accept', {
        method: 'POST',
        body: { token }
      });
      setAccepted(result);
      await onComplete();
      navigateHome();
    } catch (cause) {
      setError(apiErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={standalone ? 'dd-standalone-state' : 'dd-page dd-invitation-page'}>
      <section className="dd-setup-card dd-invitation-card" aria-labelledby="invitation-title">
        {accepted ? <CheckCircle2 aria-hidden="true" /> : <MailCheck aria-hidden="true" />}
        <p className="dd-eyebrow">Accesso al workspace</p>
        <h1 id="invitation-title">
          {accepted ? `Invito a ${accepted.name} accettato` : 'Accetta il tuo invito'}
        </h1>
        {accepted ? (
          <p>Stiamo aprendo il workspace. La pagina verrà aggiornata automaticamente.</p>
        ) : token ? (
          <>
            <p>
              Conferma per entrare nel workspace associato a questo invito. L’indirizzo email
              dell’account con cui hai effettuato l’accesso deve coincidere con quello invitato.
            </p>
            <div className="dd-form-notice">
              <ShieldCheck aria-hidden="true" />
              <span>
                L’invito non assegna privilegi amministrativi: gli accessi saranno gestiti dagli
                amministratori del workspace.
              </span>
            </div>
            {error ? (
              <p className="dd-inline-error" role="alert">
                {error}
              </p>
            ) : null}
            <Button disabled={submitting} onClick={() => void accept()}>
              {submitting ? 'Accettazione in corso…' : 'Accetta invito ed entra'}
            </Button>
          </>
        ) : (
          <>
            <p>
              Il collegamento non contiene un token di invito. Apri il link completo ricevuto via
              email oppure chiedi all’amministratore di inviarne uno nuovo.
            </p>
            <Button asChild variant="secondary">
              <a href="/">Torna alla panoramica</a>
            </Button>
          </>
        )}
      </section>
    </main>
  );
}
